import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import {
  Bell, Plus, Shield, AlertTriangle, AlertCircle, Info,
  Check, X, ChevronRight, Trash2, Settings2, User
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";

const severityConfig = {
  critical: { icon: AlertCircle, color: "#8A2A23", bg: "#FBEAE9", label: "Critical" },
  warning: { icon: AlertTriangle, color: "#805B10", bg: "#FDF3E1", label: "Warning" },
  info: { icon: Info, color: "#274F7D", bg: "#EAF0F6", label: "Info" },
};

const ruleTypeLabels = {
  score_drop_below: "Score Drops Below Threshold",
  tier_downgrade: "Tier Downgrade",
  vip_at_risk: "VIP Client At Risk",
  score_drop_amount: "Major Score Drop",
  inactivity_alert: "Valuable Client Inactive",
};

export default function ScoreAlertsPage() {
  const [alerts, setAlerts] = useState([]);
  const [rules, setRules] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("active");
  const [filterSeverity, setFilterSeverity] = useState("all");
  const [showNewRule, setShowNewRule] = useState(false);
  const [newRule, setNewRule] = useState({
    name: "", type: "score_drop_below", threshold: 40,
    severity: "warning", suggested_action: "",
    is_active: true, notify_provider: true, notify_manager: true
  });
  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      const [alertRes, ruleRes, sumRes] = await Promise.all([
        api.get(`/score-alerts?status=${filterStatus}&severity=${filterSeverity}&limit=50`),
        api.get("/score-alert-rules"),
        api.get("/score-alerts/summary"),
      ]);
      setAlerts(alertRes.data); setRules(ruleRes.data); setSummary(sumRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [filterStatus, filterSeverity]);

  const dismissAlert = async (id) => {
    await api.put(`/score-alerts/${id}/dismiss`);
    toast.success("Alert dismissed");
    fetchData();
  };

  const resolveAlert = async (id) => {
    await api.put(`/score-alerts/${id}/resolve?resolution=Reviewed and actioned`);
    toast.success("Alert resolved");
    fetchData();
  };

  const createRule = async () => {
    try {
      await api.post("/score-alert-rules", newRule);
      toast.success("Alert rule created");
      setShowNewRule(false);
      setNewRule({ name: "", type: "score_drop_below", threshold: 40, severity: "warning", suggested_action: "", is_active: true, notify_provider: true, notify_manager: true });
      fetchData();
    } catch (err) { toast.error("Failed"); }
  };

  const toggleRule = async (ruleId, isActive) => {
    await api.put(`/score-alert-rules/${ruleId}`, { is_active: !isActive });
    setRules(prev => prev.map(r => r.rule_id === ruleId ? { ...r, is_active: !isActive } : r));
  };

  const deleteRule = async (ruleId) => {
    await api.delete(`/score-alert-rules/${ruleId}`);
    toast.success("Rule deleted");
    fetchData();
  };

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] mx-auto" data-testid="score-alerts-page">
      <div className="flex items-center justify-between mb-8 animate-fadeIn">
        <div>
          <h1 className="font-['Outfit'] text-3xl font-light tracking-tight text-slate-900">Score Alerts</h1>
          <p className="text-slate-600 text-sm mt-1">Automatic notifications when client scores change</p>
        </div>
        <Button data-testid="new-rule-btn" onClick={() => setShowNewRule(true)} className="bg-slate-900 text-white hover:bg-slate-800 rounded-full h-10 px-5 text-sm">
          <Plus className="w-4 h-4 mr-1.5" /> New Alert Rule
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <div className={`border rounded-2xl p-5 ${(summary.critical || 0) > 0 ? 'bg-rose-50 border-rose-200' : 'bg-white border-slate-200'}`}>
          <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Critical</span>
          <p className={`font-['Outfit'] text-3xl font-medium mt-1 ${(summary.critical || 0) > 0 ? 'text-rose-600' : 'text-slate-900'}`}>{summary.critical || 0}</p>
        </div>
        <div className={`border rounded-2xl p-5 ${(summary.warning || 0) > 0 ? 'bg-[#FDF3E1] border-[#F3D8A2]' : 'bg-white border-slate-200'}`}>
          <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Warnings</span>
          <p className={`font-['Outfit'] text-3xl font-medium mt-1 ${(summary.warning || 0) > 0 ? 'text-amber-600' : 'text-slate-900'}`}>{summary.warning || 0}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Total Active</span>
          <p className="font-['Outfit'] text-3xl font-medium mt-1 text-slate-900">{summary.active || 0}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Resolved Today</span>
          <p className="font-['Outfit'] text-3xl font-medium mt-1 text-emerald-600">{summary.resolved_today || 0}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Active Rules</span>
          <p className="font-['Outfit'] text-3xl font-medium mt-1 text-slate-900">{summary.active_rules || 0}</p>
        </div>
      </div>

      <Tabs defaultValue="alerts">
        <TabsList className="bg-[#FAFAFA] p-1 rounded-lg border border-slate-200 mb-4">
          <TabsTrigger data-testid="tab-alerts" value="alerts" className="text-sm">
            Alerts {(summary.active || 0) > 0 && <span className="ml-1.5 w-5 h-5 rounded-full bg-rose-600 text-white text-[10px] inline-flex items-center justify-center">{summary.active}</span>}
          </TabsTrigger>
          <TabsTrigger data-testid="tab-rules" value="rules" className="text-sm">Rules ({rules.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="alerts">
          {/* Filters */}
          <div className="flex gap-3 mb-4">
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger data-testid="alert-status-filter" className="w-[140px] h-9 border-slate-200 bg-white rounded-lg text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="dismissed">Dismissed</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterSeverity} onValueChange={setFilterSeverity}>
              <SelectTrigger className="w-[140px] h-9 border-slate-200 bg-white rounded-lg text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Severity</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="info">Info</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Alert Cards */}
          <div className="space-y-3">
            {alerts.length === 0 && (
              <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center">
                <Shield className="w-12 h-12 text-[#E6E4E0] mx-auto mb-3" strokeWidth={1} />
                <p className="text-sm text-slate-400">No {filterStatus} alerts</p>
                <p className="text-xs text-rose-400 mt-1">Alerts trigger automatically when client scores change</p>
              </div>
            )}
            {alerts.map(alert => {
              const sev = severityConfig[alert.severity] || severityConfig.info;
              const SevIcon = sev.icon;
              return (
                <div key={alert.alert_id} className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid={`alert-${alert.alert_id}`}>
                  <div className="flex items-start gap-4 p-5">
                    <div className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: sev.bg }}>
                      <SevIcon className="w-5 h-5" style={{ color: sev.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-bold uppercase px-2 py-0.5 rounded-full" style={{ backgroundColor: sev.bg, color: sev.color }}>{sev.label}</span>
                        <span className="text-xs text-slate-400">{alert.rule_name}</span>
                        <span className="text-xs text-rose-400 ml-auto">{alert.created_at ? new Date(alert.created_at).toLocaleString() : ""}</span>
                      </div>
                      <p className="text-sm font-medium text-slate-900 mb-1">{alert.message}</p>
                      <div className="flex items-center gap-4 text-xs text-slate-600">
                        <span>Score: <span className="font-medium text-slate-900">{alert.score}</span></span>
                        {alert.score_delta !== 0 && (
                          <span className={alert.score_delta < 0 ? "text-rose-600" : "text-emerald-600"}>
                            {alert.score_delta > 0 ? "+" : ""}{alert.score_delta} pts
                          </span>
                        )}
                        {alert.provider_name && (
                          <span className="flex items-center gap-1"><User className="w-3 h-3" />{alert.provider_name}</span>
                        )}
                      </div>
                      {alert.suggested_action && (
                        <div className="mt-2 text-xs text-slate-600 bg-[#FAFAFA] rounded-lg px-3 py-2 italic">
                          Suggested: {alert.suggested_action}
                        </div>
                      )}
                    </div>
                    {alert.status === "active" && (
                      <div className="flex flex-col gap-1.5 flex-shrink-0">
                        <button data-testid={`resolve-${alert.alert_id}`} onClick={() => resolveAlert(alert.alert_id)}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-[#E8F0E9] text-emerald-600 hover:bg-[#d4e8d6] transition-colors">
                          <Check className="w-3 h-3" /> Resolve
                        </button>
                        <button onClick={() => dismissAlert(alert.alert_id)}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-[#FAFAFA] text-slate-400 hover:bg-rose-50 transition-colors">
                          <X className="w-3 h-3" /> Dismiss
                        </button>
                        <button onClick={() => navigate(`/clients/${alert.client_id}`)}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-[#FAFAFA] text-blue-600 hover:bg-[#EAF0F6] transition-colors">
                          View <ChevronRight className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                    {alert.status !== "active" && (
                      <span className={`text-xs px-2 py-1 rounded-full ${alert.status === "resolved" ? "bg-[#E8F0E9] text-emerald-600" : "bg-[#FAFAFA] text-slate-400"}`}>{alert.status}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="rules">
          <div className="space-y-3">
            {rules.map(rule => {
              const sev = severityConfig[rule.severity] || severityConfig.info;
              return (
                <div key={rule.rule_id} className={`bg-white border border-slate-200 rounded-2xl p-5 ${!rule.is_active ? 'opacity-50' : ''}`} data-testid={`rule-${rule.rule_id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: sev.bg }}>
                        <Bell className="w-4 h-4" style={{ color: sev.color }} />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-900">{rule.name}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-slate-400">{ruleTypeLabels[rule.type] || rule.type}</span>
                          {rule.threshold > 0 && <span className="text-xs px-1.5 py-0.5 rounded bg-[#FAFAFA] text-slate-600">Threshold: {rule.threshold}</span>}
                          <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ backgroundColor: sev.bg, color: sev.color }}>{sev.label}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex gap-1.5 text-[10px] text-slate-400">
                        {rule.notify_provider && <span className="px-1.5 py-0.5 rounded bg-[#FAFAFA]">Provider</span>}
                        {rule.notify_manager && <span className="px-1.5 py-0.5 rounded bg-[#FAFAFA]">Manager</span>}
                      </div>
                      <Switch checked={rule.is_active} onCheckedChange={() => toggleRule(rule.rule_id, rule.is_active)} />
                      <button data-testid={`delete-rule-${rule.rule_id}`} onClick={() => deleteRule(rule.rule_id)} className="text-slate-400 hover:text-rose-600 p-1"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                  {rule.suggested_action && (
                    <p className="text-xs text-rose-400 mt-2 ml-11">Action: {rule.suggested_action}</p>
                  )}
                </div>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* New Rule Dialog */}
      <Dialog open={showNewRule} onOpenChange={setShowNewRule}>
        <DialogContent className="sm:max-w-[520px] bg-white border-slate-200 rounded-2xl p-8" data-testid="new-rule-modal">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-xl font-medium">New Alert Rule</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <Input data-testid="rule-name" value={newRule.name} onChange={e => setNewRule(p => ({...p, name: e.target.value}))} placeholder="Rule name" className="h-11 border-slate-200 rounded-lg" />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-400 mb-1 block">Alert Type</label>
                <Select value={newRule.type} onValueChange={v => setNewRule(p => ({...p, type: v}))}>
                  <SelectTrigger className="h-11 border-slate-200 rounded-lg text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(ruleTypeLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-400 mb-1 block">Severity</label>
                <Select value={newRule.severity} onValueChange={v => setNewRule(p => ({...p, severity: v}))}>
                  <SelectTrigger className="h-11 border-slate-200 rounded-lg text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="warning">Warning</SelectItem>
                    <SelectItem value="info">Info</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-400 mb-1 block">Threshold (score or days)</label>
              <Input data-testid="rule-threshold" type="number" value={newRule.threshold} onChange={e => setNewRule(p => ({...p, threshold: Number(e.target.value)}))} className="h-11 border-slate-200 rounded-lg" />
            </div>
            <Input data-testid="rule-action" value={newRule.suggested_action} onChange={e => setNewRule(p => ({...p, suggested_action: e.target.value}))} placeholder="Suggested action when alert triggers..." className="h-11 border-slate-200 rounded-lg" />
            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm text-slate-600">
                  <Switch checked={newRule.notify_provider} onCheckedChange={v => setNewRule(p => ({...p, notify_provider: v}))} /> Notify Provider
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-600">
                  <Switch checked={newRule.notify_manager} onCheckedChange={v => setNewRule(p => ({...p, notify_manager: v}))} /> Notify Manager
                </label>
              </div>
            </div>
            <Button data-testid="create-rule-btn" onClick={createRule} className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-full h-11">Create Alert Rule</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
